import {
    IInteractionMaximumMetricParameters,
    IInteractionMinimumMetricParameters,
    IInteractionsGetQueryParameters,
    Sentiment,
} from "@edgetier/watchtower-types";
import type { VariableTokenKind } from "@edgetier/global-components";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faAnalytics,
    faAngry,
    faBallotCheck,
    faCalendar,
    faCircleCheck,
    faCircleSmall,
    faComments,
    faFaceSmile,
    faGlobe,
    faRocketLaunch,
    faSmile,
    faSpellCheck,
    faStarHalfStroke,
    faStopwatch,
    faTag,
    faTally4,
    faThumbsUp,
    faUser,
    faUserCheck,
    faUserGroup,
} from "@fortawesome/pro-solid-svg-icons";
import {
    faAgentGroup,
    faFaConfusionSolid,
    faHandHoldingHeartSolid,
    faTagExternal,
    faTagAi,
} from "@awesome.me/kit-67bf90e9a4/icons/kit/custom";
import { EXPERIENCE_DRIVERS } from "constants/experience-drivers";
import { ExperienceDriver } from "types-for/experience-drivers/experience-drivers.types";

export const insaneBigLimit = 99999;

export enum FilterGroups {
    Interaction = "Interaction",
    PeopleQA = "People / QA",
    Emotion = "Emotion",
    ExperienceDrivers = "Experience Drivers",
    SurveyScore = "Survey / Score",
    Count = "Count",
    Time = "Time",
    AITag = "AI Tag",
    ExternalTag = "External Tag",
    PhraseTag = "Phrase Tags",
    AgentGroups = "Agent Groups",
    AutomaticTags = "AI Tags",
    ExternalGroups = "External Tags",
    General = "General",
}

export enum OldFilterGroups {
    Emotions = "Emotions",
    General = "General",
    Metrics = "Metrics",
    TagGroups = "Tag Groups",
    ExternalGroups = "System Tags",
    PhraseSearchGroups = "Phrase Tags",
    AgentGroups = "Agent Groups",
    AutomaticTags = "Automatic Tags",
}

export const OldFilterGroupsOrder = {
    [OldFilterGroups.General]: 0,
    [OldFilterGroups.Emotions]: 1,
    [OldFilterGroups.Metrics]: 2,
    [OldFilterGroups.TagGroups]: 3,
    [OldFilterGroups.AgentGroups]: 4,
    [OldFilterGroups.PhraseSearchGroups]: 5,
    [OldFilterGroups.ExternalGroups]: 6,
    [OldFilterGroups.AutomaticTags]: 7,
};

export const FilterGroupsOrder: Record<FilterGroups, number> = {
    [FilterGroups.Interaction]: 0,
    [FilterGroups.PeopleQA]: 1,
    [FilterGroups.Emotion]: 2,
    [FilterGroups.ExperienceDrivers]: 3,
    [FilterGroups.SurveyScore]: 4,
    [FilterGroups.Count]: 5,
    [FilterGroups.Time]: 6,
    [FilterGroups.AgentGroups]: 7,
    [FilterGroups.AutomaticTags]: 8,
    [FilterGroups.ExternalGroups]: 9,
    [FilterGroups.AITag]: 10,
    [FilterGroups.ExternalTag]: 11,
    [FilterGroups.PhraseTag]: 12,
    [FilterGroups.General]: 13,
};

export const FilterGroupsIcons = {
    [FilterGroups.Interaction]: faComments,
    [FilterGroups.PeopleQA]: faUserGroup,
    [FilterGroups.Emotion]: faFaceSmile,
    [FilterGroups.ExperienceDrivers]: faRocketLaunch,
    [FilterGroups.SurveyScore]: faStarHalfStroke,
    [FilterGroups.Count]: faTally4,
    [FilterGroups.Time]: faStopwatch,
    [FilterGroups.AITag]: faTagAi,
    [FilterGroups.ExternalTag]: faTagExternal,
    [FilterGroups.PhraseTag]: faTag,
    [FilterGroups.AgentGroups]: faAgentGroup,
    [FilterGroups.AutomaticTags]: faTagAi,
    [FilterGroups.ExternalGroups]: faTagExternal,
};

export const EmotionsGroupItemOrder = {
    [Sentiment.Frustration]: 0,
    [Sentiment.Confusion]: 1,
    [Sentiment.Gratitude]: 2,
    [Sentiment.Praise]: 3,
    [Sentiment.Empathy]: 4,
};

export const InteractionFiltersConfigurations: Record<
    | keyof IInteractionsGetQueryParameters
    | keyof IInteractionMaximumMetricParameters
    | keyof IInteractionMinimumMetricParameters,
    { label: string }
> = {
    analysisTimePeriod: { label: "Time Period" },
    agentExperienceRating: { label: "Agent Handling - Experience Driver" },
    agentId: { label: "Agent" },
    anomalyId: { label: "Anomaly" },
    contactId: { label: "Contact" },
    agentStateType: { label: "Agent State" },
    agentTagGroupId: { label: "Agent Tag Group" },
    agentTagId: { label: "Agent Tag" },
    botExperienceRating: { label: "Bot Handling - Experience Driver" },
    endDateTime: { label: "Time Period" },
    primaryAgentsOnly: { label: "Primary Agent Only" },
    groupBy: { label: "Group By" },
    hasFrustration: { label: "Frustration" },
    hasGratitude: { label: "Gratitude" },
    hasPraise: { label: "Praise" },
    hasConfusion: { label: "Confusion" },
    hasEmpathy: { label: "Empathy" },
    highlightMinimumWords: { label: "Highlight Minimum Words" },
    highlightMaximumWords: { label: "Highlight Maximum Words" },
    interactionState: { label: "Interaction State" },
    interactionType: { label: "Interaction Type" },
    isAgentSearch: { label: "Agent Search" },
    isBotSearch: { label: "Bot Search" },
    isCustomerSearch: { label: "Customer Search" },
    isMessageSearch: { label: "Message Search" },
    isMetadataSearch: { label: "Metadata Search" },
    isQueryResolved: { label: "Survey Response - Resolved" },
    issue: { label: "Issue" },
    isSystemSearch: { label: "System Search" },
    languageIsoCode: { label: "Language" },
    limit: { label: "Limit" },
    messageDirections: { label: "Message Directions" },
    orderBy: { label: "Order By" },
    parameterGroupId: { label: "Parameter Group" },
    productExperienceRating: { label: "Product/Process - Experience Driver" },
    scorecardId: { label: "Scorecard" },
    search: { label: "Search" },
    searchType: { label: "Search Type" },
    similarityThreshold: { label: "Similarity Threshold" },
    startDateTime: { label: "Time Period" },
    tagGroupId: { label: "Tag Group" },
    tagId: { label: "Tag" },
    textSearchType: { label: "Text Search Type" },
    timeZone: { label: "Time Zone" },
    webSearchSyntax: { label: "Web Search Syntax" },
    writingIssueType: { label: "Writing Issue Type" },
    beforeInteractionId: { label: "Before Interaction" },
    afterInteractionId: { label: "After Interaction" },
    relativeDateTime: { label: "Relative Date Time" },
    maximumInteractionCount: { label: "Interaction Count" },
    maximumMessagesCount: { label: "Messages Count" },
    maximumMaximumAgentResponseTimeSeconds: { label: "Agent Response Time" },
    maximumMeanCustomerResponseTimeSeconds: { label: "Customer Response Time" },
    maximumMeanResponseSeconds: { label: "Mean Response Time" },
    maximumAgentMessagesCount: { label: "Agent Messages Count" },
    maximumAgentFirstResponseTimeSeconds: { label: "Agent First Response Time" },
    maximumAgentSatisfaction: { label: "Agent Satisfaction" },
    maximumAgentScore: { label: "Agent Score" },
    maximumBotMessagesCount: { label: "Bot Messages Count" },
    maximumCustomerMessagesCount: { label: "Customer Messages Count" },
    maximumCustomerSatisfaction: { label: "Customer Satisfaction" },
    maximumQueueTimeSeconds: { label: "Queue Time" },
    maximumHandlingTimeSeconds: { label: "Handling Time" },
    maximumExperienceScore: { label: "Experience Score" },
    maximumNetPromoterScore: { label: "Net Promoter Score" },
    maximumFirstResponseTimeSeconds: { label: "First Response Time" },
    maximumServiceLevelAgreementPercentage: { label: "Service Level Agreement Percentage" },
    minimumInteractionCount: { label: "Interaction Count" },
    minimumMaximumAgentResponseTimeSeconds: { label: "Agent Response Time" },
    minimumMeanCustomerResponseTimeSeconds: { label: "Customer Response Time" },
    minimumMeanResponseSeconds: { label: "Mean Response Time" },
    minimumAgentMessagesCount: { label: "Agent Messages Count" },
    minimumAgentFirstResponseTimeSeconds: { label: "Agent First Response Time" },
    minimumAgentSatisfaction: { label: "Agent Satisfaction" },
    minimumAgentScore: { label: "Agent Score" },
    minimumBotMessagesCount: { label: "Bot Messages Count" },
    minimumCustomerMessagesCount: { label: "Customer Messages Count" },
    minimumCustomerSatisfaction: { label: "Customer Satisfaction" },
    minimumQueueTimeSeconds: { label: "Queue Time" },
    minimumHandlingTimeSeconds: { label: "Handling Time" },
    minimumExperienceScore: { label: "Experience Score" },
    minimumMessagesCount: { label: "Messages Count" },
    minimumNetPromoterScore: { label: "Net Promoter Score" },
    minimumFirstResponseTimeSeconds: { label: "First Response Time" },
    minimumServiceLevelAgreementPercentage: { label: "Service Level Agreement Percentage" },
    resolutionStatus: { label: "Resolution Status" },
    reviewStatus: { label: "Review Result" },
    reviewerId: { label: "Reviewers" },
    scorecardEvaluationsCoverage: { label: "Reviews Coverage" },
    scorecardEvaluationState: { label: "Review Status" },
};

export const EXPLORE_FILTERS_SCHEMA_VERSION = "1.0.0";

export const SPOTLIGHT_EXPANSION_PARAMETER_GROUP_URL_KEY = "spotlight-expansion.parameterGroupId";

// Error code returned by the API (404) when a request references a saved filter (parameter group) that is no longer
// available — its TTL expired, the cache was flushed, or the user followed a stale bookmark / old link.
export const PARAMETER_GROUP_NOT_FOUND_ERROR = "PARAMETER_GROUP_NOT_FOUND";

// Design-approved warning copy shown for an expired Phrase Search filter (parameter group).
export const PARAMETER_GROUP_NOT_FOUND_TITLE = "Phrase Search filter expired";
export const PARAMETER_GROUP_NOT_FOUND_MESSAGE =
    "This link included a Phrase Search filter that's no longer available. Re-apply it in the filter bar if needed.";

/**
 * The icon of each Explore filter, keyed by its query parameter (`metric` covers every metric filter). The one
 * source for Explore's filter list and the Ask Spotlight variable pills, so the two cannot drift apart.
 */
export const ExploreFilterIcons = {
    agentId: faUser,
    languageIsoCode: faGlobe,
    reviewerId: faBallotCheck,
    scorecardId: faBallotCheck,
    interactionType: faComments,
    resolutionStatus: faCircleCheck,
    reviewStatus: faBallotCheck,
    scorecardEvaluationState: faCircleSmall,
    writingIssueType: faSpellCheck,
    isQueryResolved: faUserCheck,
    primaryAgentsOnly: faUserCheck,
    hasFrustration: faAngry,
    hasGratitude: faSmile,
    hasPraise: faThumbsUp,
    // The kit ships its own `IconDefinition` type, so its icons need widening to the root one.
    hasEmpathy: faHandHoldingHeartSolid as IconDefinition,
    hasConfusion: faFaConfusionSolid as IconDefinition,
    productExperienceRating: EXPERIENCE_DRIVERS[ExperienceDriver.ProductProcess].icon,
    botExperienceRating: EXPERIENCE_DRIVERS[ExperienceDriver.BotHandling].icon,
    agentExperienceRating: EXPERIENCE_DRIVERS[ExperienceDriver.AgentHandling].icon,
    metric: faAnalytics,
};

/**
 * The icon for each variable kind, from its Explore filter. Keyed by every kind so a new kind fails to compile until
 * its icon is decided. `emotion` and `field` depend on the subtype, and tag-backed kinds on their tag group (see
 * `getVariablePillProps`); `timePeriod` has no Explore filter icon.
 */
export const VariableKindIcons: Readonly<Record<VariableTokenKind, IconDefinition | undefined>> = {
    agent: ExploreFilterIcons.agentId,
    agentGroup: faAgentGroup as IconDefinition,
    tag: faTag,
    reviewer: ExploreFilterIcons.reviewerId,
    scorecard: ExploreFilterIcons.scorecardId,
    language: ExploreFilterIcons.languageIsoCode,
    metric: ExploreFilterIcons.metric,
    emotion: undefined,
    field: undefined,
    timePeriod: faCalendar,
};

/** Icons for `emotion` variables, keyed by the Explore filter the subtype names. */
export const EmotionVariableIcons: Record<string, IconDefinition> = {
    hasFrustration: ExploreFilterIcons.hasFrustration,
    hasGratitude: ExploreFilterIcons.hasGratitude,
    hasPraise: ExploreFilterIcons.hasPraise,
    hasEmpathy: ExploreFilterIcons.hasEmpathy,
    hasConfusion: ExploreFilterIcons.hasConfusion,
};

/** Icons for `field` variables, keyed by the Explore filter the subtype names. */
export const FieldVariableIcons: Record<string, IconDefinition> = {
    interactionType: ExploreFilterIcons.interactionType,
    resolutionStatus: ExploreFilterIcons.resolutionStatus,
    reviewStatus: ExploreFilterIcons.reviewStatus,
    scorecardEvaluationState: ExploreFilterIcons.scorecardEvaluationState,
    writingIssueType: ExploreFilterIcons.writingIssueType,
    isQueryResolved: ExploreFilterIcons.isQueryResolved,
    productExperienceRating: ExploreFilterIcons.productExperienceRating,
    botExperienceRating: ExploreFilterIcons.botExperienceRating,
    agentExperienceRating: ExploreFilterIcons.agentExperienceRating,
};
